// "Did you go? How was it?" (R-14 app half): the queue of outings to check in on, computed ONCE per app open.
// An app open = a new browsing session (sessionStorage starts empty); the queue for that open is kept in sessionStorage with
// the user id it was gathered for (B-161: shown to no one else; lib/owner.ts drops it when the person changes), so
// moving between screens shows the same one card and nothing new joins it until the next open. Rules: lib/checkinCore.ts.
// Sources (all the person's own rows, read under Row Level Security or through the `plan` function):
//   calendar adds (calendar_adds -> items; expired items stay readable through items_read_mine_added),
//   saved things (lib/saved.ts: undated plans; the item's own time), itineraries (dated plans; the stop's own time).
// Answered = taste_events went / didnt_go / pad / not_for_me, plus the older feedback table. Skips live on this device.
import { useSyncExternalStore } from 'react'
import * as Sentry from '@sentry/react'
import { ITEM_COLUMNS, supabase } from './api'
import { getItinerary } from './itinerary'
import { loadSaved, savedEntriesNow, savedPlansNow } from './saved'
import { addSkip, buildQueue, overAt, slotOverAt, WINDOW_DAYS, type Outing } from './checkinCore'
import { markBrowseStale } from './browseCache'
import { onOwnerChange, ownerNow, watchOwner } from './owner'
import type { PlanItemData } from './plan'

export type CheckOuting = Outing<PlanItemData>
const OPEN_KEY = 'mg_checkin_open' // sessionStorage: this open's queue (item snapshots), or [] when there was nothing
const SKIPS_KEY = 'mg_checkin_skips' // localStorage: { itemId: times skipped }
const MAX_DATED_PLANS = 4
watchOwner(supabase.auth)

// uid: whose queue this is (B-161). A queue is shown only to the person it was gathered for.
type State = { queue: CheckOuting[] | null; uid: string | null }
let state: State = { queue: null, uid: null }
const subs = new Set<() => void>()
const set = (p: Partial<State>) => { state = { ...state, ...p }; subs.forEach((f) => f()) }
const subscribe = (f: () => void) => { subs.add(f); return () => { subs.delete(f) } }
const NONE: State = { queue: [], uid: null }
/** The queue, only for the person it was gathered for: a queue left from another user id is never shown (B-161). */
const view = () => (state.queue && state.queue.length && state.uid !== ownerNow() ? NONE : state)
export const useCheckin = () => useSyncExternalStore(subscribe, view)

// the kept queue names its owner: before 10/15 it did not, and another tab reused the old account's queue under a new one
type Kept = { uid: string; q: CheckOuting[] }
const ss = {
  get: (uid: string): CheckOuting[] | null => {
    try {
      const v = JSON.parse(sessionStorage.getItem(OPEN_KEY) ?? 'null') as Kept | null
      return v && !Array.isArray(v) && v.uid === uid && Array.isArray(v.q) ? v.q : null
    } catch { return null }
  },
  put: (uid: string | null, q: CheckOuting[]) => {
    try {
      if (uid) sessionStorage.setItem(OPEN_KEY, JSON.stringify({ uid, q } satisfies Kept))
      else sessionStorage.removeItem(OPEN_KEY)
    } catch { /* the card still shows this time */ }
  },
}
const readSkips = (): Record<string, number> => { try { return JSON.parse(localStorage.getItem(SKIPS_KEY) ?? '{}') as Record<string, number> } catch { return {} } }
const writeSkips = (s: Record<string, number>) => { try { localStorage.setItem(SKIPS_KEY, JSON.stringify(s)) } catch { /* convenience */ } }

let started: Promise<void> | null = null
let savedForOld = false // lib/saved.ts still holds what it loaded for the previous person
let run = 0
/** Start this open's queue (a no-op after the first call in this open, and for someone with no session yet). */
export function startCheckin(): Promise<void> {
  if (started) return started
  const mine = ++run
  started = (async () => {
    try {
      const { data } = await supabase.auth.getSession()
      const uid = data.session?.user.id ?? null
      if (mine !== run) return
      if (!uid) { set({ queue: [], uid: null }); return }
      const kept = ss.get(uid)
      if (kept) { set({ queue: kept, uid }); return }
      const q = await gather(uid)
      // the person changed while this was gathered (Delete my data in another tab): this queue is not theirs
      if (mine !== run || ownerNow() !== uid) return
      ss.put(uid, q)
      set({ queue: q, uid })
    } catch (e) {
      Sentry.captureException(e)
      if (mine === run) set({ queue: [] }) // nothing kept: a later open tries again
    }
  })()
  return started
}

// B-161: another person in this tab: the old queue goes (its key went with lib/owner.ts) and, if the card is on screen,
// the new person's own queue is gathered (a new visitor has none)
onOwnerChange(() => {
  run++
  savedForOld = true
  started = null
  set({ queue: [], uid: ownerNow() })
  if (subs.size) void startCheckin()
})

async function gather(uid: string): Promise<CheckOuting[]> {
  const now = Date.now()
  const out: CheckOuting[] = []
  const [cal, said, fb, nfm] = await Promise.all([
    supabase.from('calendar_adds').select('item_id').eq('user_id', uid).gte('created_at', new Date(now - 120 * 86400e3).toISOString()).limit(200),
    supabase.from('taste_events').select('item_id,kind,context').eq('user_id', uid).in('kind', ['went', 'didnt_go', 'pad', 'not_for_me', 'how_was_it']).not('item_id', 'is', null).limit(1000),
    supabase.from('feedback').select('item_id').eq('user_id', uid).limit(1000),
    // a not for me kept as a swipe only (the older deck) counts as answered too
    supabase.from('swipes').select('item_id').eq('user_id', uid).eq('value', -2).limit(1000),
  ])
  // a Not for me that was taken back (signal undo_not_for_me marks it `undone_at`) is not an answer
  const saidRows = ((said.data ?? []) as { item_id: string; kind: string; context?: { undone_at?: string } | null }[]).filter((r) => !(r.kind === 'not_for_me' && r.context?.undone_at))
  const done = new Set<string>([...saidRows, ...(fb.data ?? []), ...(nfm.data ?? [])].map((r) => (r as { item_id: string }).item_id))

  // calendar adds: the item's own time
  const calIds = [...new Set((cal.data ?? []).map((r) => (r as { item_id: string }).item_id))].filter((id) => !done.has(id))
  if (calIds.length) {
    const { data: items } = await supabase.from('items').select(ITEM_COLUMNS).in('id', calIds.slice(0, 100))
    for (const it of (items ?? []) as unknown as PlanItemData[]) {
      const at = overAt(it)
      if (at != null) out.push({ item: it, at, source: 'calendar' })
    }
  }
  // saved things (undated plans), as Saved already loads them (its plan list also gives the itineraries below)
  // (the Saved store in memory may still be the previous person's after a change of person in this tab: reload it then)
  if (savedForOld || !savedEntriesNow() || !savedPlansNow()) {
    await loadSaved({ onlyWithSession: true }).catch(() => {})
    savedForOld = false
  }
  for (const e of savedEntriesNow() ?? []) {
    const at = overAt(e.item)
    if (at != null) out.push({ item: e.item, at, source: 'saved' })
  }
  // itineraries: dated plans that started inside the window and are not all ahead
  const today = new Date(now).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' })
  const from = new Date(now - WINDOW_DAYS * 86400e3).toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' })
  const dated = (savedPlansNow() ?? []).filter((p) => p.mine !== false && p.start_date && p.start_date <= today && (p.end_date ?? p.start_date) >= from && (p.item_count ?? 1) > 0)
    .sort((a, b) => String(b.start_date).localeCompare(String(a.start_date))).slice(0, MAX_DATED_PLANS)
  const got = await Promise.all(dated.map((p) => getItinerary(p.id).catch(() => null)))
  for (const g of got) for (const d of g?.days ?? []) for (const s of d.items) {
    if (!s.item) continue
    const at = slotOverAt({ start: s.start, end: s.end }, s.item)
    if (at != null) out.push({ item: s.item, at, source: 'plan' })
  }
  return buildQueue(out, done, readSkips(), now)
}
/** The person answered (yes + the pad, or no): this outing leaves the queue, and Browse asks again next time (B-163). */
export function answered(itemId: string) { drop(itemId); markBrowseStale() }
/** Skip: asked again on a later open, at most MAX_SKIPS times in all. */
export function skip(itemId: string) {
  writeSkips(addSkip(readSkips(), itemId))
  drop(itemId)
}
function drop(itemId: string) {
  const queue = (state.queue ?? []).filter((o) => o.item.id !== itemId)
  if (state.uid && state.uid === ownerNow()) ss.put(state.uid, queue)
  set({ queue })
}
